import {
  buildSuiteCounts,
  type SuiteStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { SuiteExecutionsTable } from '@/features/executions/components/SuiteExecutionsTable';
import { querySuiteExecutionStatusSummary } from '@/features/executions/data';
import { EntityTrendsPageShell } from '@/features/shared/components/EntityTrendsPageShell';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { SuitesTable } from '@/features/suites/components/SuitesTable';
import { querySuites, querySuiteSummary } from '@/features/suites/data/suites';
import { suiteExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { gridPaths, queryKeys } from '@/shared/hooks';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  SuiteExecutionStatusSummaryDto,
  SuiteListItemDto,
  SuiteSummaryDto,
} from '@flowqa/shared';
import type { ReactElement } from 'react';

export const SuitesPage = (): ReactElement => {
  const viewModel = useEntityTrendsPage<
    SuiteListItemDto,
    SuiteSummaryDto | SuiteExecutionStatusSummaryDto
  >({
    loadingMessage: 'Loading suites...',
    metricsScope: 'suite',
    gridPath: gridPaths.suites,
    executionGridPath: gridPaths.suiteExecutions,
    entitySearchField: 'name',
    executionSearchField: 'suiteName',
    statusOptions: suiteExecutionSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<SuiteStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteExecutionSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.suiteSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.suiteExecutionSummaryPrefix,
    queryLatestSummary: querySuiteSummary,
    queryExecutionSummary: querySuiteExecutionStatusSummary,
    buildCounts: buildSuiteCounts,
    selectThresholds: (thresholds) => thresholds.suites,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
        tableDeltasEnabled,
      },
    ): Promise<QueryResult<SuiteListItemDto>> =>
      querySuites(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          project: true,
          latestExecution: true,
          latestTestResults: { counts: true },
          metrics: true,
          metricDeltas: deltasEnabled && tableDeltasEnabled,
        },
      }),
  });

  return (
    <EntityTrendsPageShell
      viewModel={viewModel}
      title="Suites"
      addButtonLabel="Add Suite"
      searchAriaLabel="Search suites"
      chartTabsAriaLabel="Suites chart tabs"
      pageTabsAriaLabel="Suites page tabs"
      entityTabLabel="Suites"
      chartEmptyStateTitle="No suite executions found"
      entityTableNoRowsText="No suites found"
      executionTableNoRowsText="No suite executions found"
      persistKey="suites-table"
      executionPersistKey="suite-executions-table"
      renderEntityTable={({
        queryKey,
        queryFn,
        fixedFilters,
        showDeltas,
        persistKey,
        noRowsText,
      }) => (
        <SuitesTable
          persistKey={persistKey}
          queryKey={queryKey}
          queryFn={queryFn}
          pageSizeOptions={[10]}
          initialPageSize={10}
          disablePageSizeSelect
          showDeltas={showDeltas}
          fixedFilters={fixedFilters}
          noRowsText={noRowsText}
        />
      )}
      renderExecutionsTable={({ fixedFilters, persistKey, noRowsText }) => (
        <SuiteExecutionsTable
          persistKey={persistKey}
          fixedFilters={fixedFilters}
          pageSizeOptions={[10]}
          initialPageSize={10}
          disablePageSizeSelect
          noRowsText={noRowsText}
        />
      )}
    />
  );
};
